import type { ReactElement, ReactNode } from 'react'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'

export interface ActionBarAction {
  /** Icon shown in the button (and as sole content on mobile). */
  icon: ReactElement
  /** Label shown next to icon on desktop; used for aria-label when not provided. */
  label: string
  /** Click handler. */
  onClick?: () => void
  /** Override for screen readers when different from label (e.g. "Upload file"). */
  ariaLabel?: string
  /** Whether the action is disabled. */
  disabled?: boolean
}

export interface ActionBarProps {
  /** Content on the left (e.g. breadcrumb, title, nav item). */
  content: ReactNode
  /** Action buttons on the right. On desktop: outlined primary with icon + label; on mobile: icon-only. */
  actions: ActionBarAction[]
}

function ActionBarActionButton({ action }: { action: ActionBarAction }) {
  const ariaLabel = action.ariaLabel ?? action.label

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="sm"
        leadingIcon={action.icon}
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={ariaLabel}
        className="hidden lg:inline-flex gap-1 !px-3"
      >
        {action.label}
      </Button>
      <IconButton
        variant="outlined"
        size="sm"
        icon={action.icon}
        aria-label={ariaLabel}
        onClick={action.onClick}
        disabled={action.disabled}
        className="lg:hidden !p-1.5"
      />
    </>
  )
}

/**
 * ActionBar — horizontal bar with content and action buttons.
 * Desktop (lg+): full buttons with icon + label. Below lg: icon-only outlined buttons.
 */
export function ActionBar({ content, actions }: ActionBarProps) {
  return (
    <div className="flex w-full min-w-0 items-center gap-4 px-4 py-3 md:px-0 lg:gap-3">
      <div className="min-w-0 flex-1">{content}</div>

      {/* Fixing to 28px height to make sure the height is the same as the button height */}
      <div className="flex min-h-[28px] shrink-0 items-center justify-end gap-2 lg:gap-3">
        {actions.map((action) => (
          <ActionBarActionButton key={action.label} action={action} />
        ))}
      </div>
    </div>
  )
}

export default ActionBar
